import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, eur, dateFmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { useI18n } from "@/i18n";
import { PageHeader, EmptyState, StatusBadge, Th, Td, SortTh, useSortable, NumberInput } from "@/components/common";
import { ListFilter, useListFilter, csvMoney } from "@/components/common/ListFilter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SearchSelect } from "@/components/SearchSelect";
import { TaxCaseCard } from "@/components/orders/TaxCaseParts";
import { toast } from "sonner";
import { ShoppingCart, Plus, Trash2, Upload, AlertTriangle, PackageX, Store, ReceiptEuro, Copy, Download } from "lucide-react";

export default function Orders() {
  const { user } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const canEdit = can(user, "orders:edit");
  const [rows, setRows] = useState(null);
  const [channels, setChannels] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [variants, setVariants] = useState([]);
  const [availMap, setAvailMap] = useState({});
  const [secondMap, setSecondMap] = useState({});
  const [bundles, setBundles] = useState([]);
  const [channelFilter, setChannelFilter] = useState("alle");
  const [showCancelled, setShowCancelled] = useState(false);
  const [productQ, setProductQ] = useState("");
  const [exporting, setExporting] = useState(false);
  const [open, setOpen] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [f, setF] = useState({ channel: "B2B", organization_id: "", po_number: "", contact: "", shipping_address: "", payment_terms: "", currency: "EUR", exchange_rate: 1, notes: "", lines: [] });

  const load = useCallback(() => {
    api.get("/orders").then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/sales-channels").then((r) => setChannels(r.data)).catch(() => {});
    api.get("/organizations").then((r) => setOrgs([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "de")))).catch(() => {});
    api.get("/variants").then((r) => setVariants([...r.data].sort((a, b) => (a.product_name || "").localeCompare(b.product_name || "", "de") || (a.sku || "").localeCompare(b.sku || "", "de")))).catch(() => {});
    api.get("/inventory").then((r) => { const m = {}, s = {}; r.data.forEach((x) => { m[x.variant_id] = x.available; s[x.variant_id] = x.second_choice; }); setAvailMap(m); setSecondMap(s); }).catch(() => {});
    api.get("/bundles").then((r) => setBundles([...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "de")))).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const addLine = () => setF({ ...f, lines: [...f.lines, { _k: crypto.randomUUID(), kind: "variant", ref: "", quantity: 1, unit_price: "", quality: "good" }] });
  const priceFor = (kind, ref, quality = "good") => {
    if (!ref) return "";
    const disc = pricing?.discount_percent || 0;
    if (kind === "bundle") {
      if (pricing?.bundle_prices?.[ref] != null) return pricing.bundle_prices[ref];
      const b = bundles.find((x) => x.id === ref); return Math.round((b?.price_net || 0) * (1 - disc / 100) * 100) / 100;
    }
    const v = variants.find((x) => x.id === ref);
    if (quality === "second_choice") return v?.price_net_second != null ? v.price_net_second : (v?.price_net || 0);
    if (pricing?.variant_prices?.[ref] != null) return pricing.variant_prices[ref];
    return Math.round((v?.price_net || 0) * (1 - disc / 100) * 100) / 100;
  };
  // Auswahloptionen einer Auftragsposition: Bundles bzw. Varianten (A-Ware, 2. Wahl, digital).
  const lineOptions = (l) => {
    if (l.kind === "bundle") {
      return bundles.filter((x) => (x.sellable ?? 0) > 0)
        .map((x) => ({ value: x.id, label: `${x.name} · ${x.sku} · ${t("inv.available")}: ${x.sellable ?? 0}` }));
    }
    const isSecond = l.quality === "second_choice";
    const stockOf = (x) => (isSecond ? secondMap[x.id] : availMap[x.id]) ?? 0;
    const availLabel = (x) => {
      if (x.is_digital) return t("common.digital");
      return `${isSecond ? t("ord.secondShort") : t("inv.available")}: ${stockOf(x)}`;
    };
    return variants.filter((x) => (isSecond ? stockOf(x) > 0 : x.is_digital || stockOf(x) > 0))
      .map((x) => ({ value: x.id, label: `${x.product_name} · ${x.sku} · ${availLabel(x)}` }));
  };
  // Preiskontext-Wechsel (Kunde/Kanal/Währung) → alle Positionen erneut serverseitig auflösen.
  const linesRef = useRef(f.lines);
  linesRef.current = f.lines;
  useEffect(() => {
    const lines = linesRef.current || [];
    if (!lines.some((l) => l.ref)) return;
    (async () => {
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].ref && !lines[i]._manual) await resolveLine(i, lines[i]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.organization_id, f.channel, f.currency]);

  const upd = (i, k, v) => {
    const l = [...f.lines]; l[i] = { ...l[i], [k]: v };
    if (k === "kind") l[i].quality = "good";
    if (k === "ref" || k === "kind" || k === "quality") {
      const p = priceFor(l[i].kind, l[i].ref, l[i].quality); if (p !== "") l[i].unit_price = p;
      l[i]._manual = false;
      l[i]._prov = null;
      const src = l[i].kind === "bundle" ? bundles.find((x) => x.id === l[i].ref) : variants.find((x) => x.id === l[i].ref);
      if (src && src.tax_rate != null) l[i].tax_rate = src.tax_rate;
      if (l[i].ref) resolveLine(i, l[i]);
    }
    if (k === "unit_price") l[i]._manual = true;
    if (k === "override_reason") l[i].override_reason = v;
    setF({ ...f, lines: l });
  };
  // Preisvorschau kommt aus derselben Server-Engine wie der Auftrag (Client rechnet nicht).
  const resolveLine = async (i, line) => {
    try {
      const r = await api.post("/sales-promotions/resolve-line", {
        organization_id: f.channel === "B2B" ? (f.organization_id || null) : null,
        variant_id: line.kind === "bundle" ? null : line.ref,
        bundle_id: line.kind === "bundle" ? line.ref : null,
        quality: line.quality || "good", currency: f.currency });
      setF((prev) => ({ ...prev, lines: prev.lines.map((x, j) => j === i && x.ref === line.ref
        ? { ...x, unit_price: r.data.unit_price, _manual: false, _prov: r.data } : x) }));
    } catch { /* Anzeige bleibt beim lokalen Vorschlag; der Server entscheidet beim Speichern */ }
  };
  const rm = (i) => setF({ ...f, lines: f.lines.filter((_, j) => j !== i) });

  const save = async () => {
    try {
      const lines = f.lines.map((l) => l.kind === "bundle"
        ? { bundle_id: l.ref, name: bundles.find((b) => b.id === l.ref)?.name || "", quantity: Number(l.quantity) || 1, unit_price: Number(l.unit_price) || 0, tax_rate: Number(l.tax_rate) ?? 19, price_override: !!l._manual, override_reason: l.override_reason || "" }
        : { variant_id: l.ref, name: variants.find((v) => v.id === l.ref)?.sku || "", quantity: Number(l.quantity) || 1, unit_price: Number(l.unit_price) || 0, tax_rate: Number(l.tax_rate) ?? 19, quality: l.quality || "good", price_override: !!l._manual, override_reason: l.override_reason || "" });
      const r = await api.post("/orders", { ...f, lines });
      toast.success(t("ord.created")); setOpen(false);
      setF({ channel: "B2B", organization_id: "", po_number: "", contact: "", shipping_address: "", payment_terms: "", currency: "EUR", exchange_rate: 1, notes: "", lines: [] });
      nav(`/auftraege/${r.data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const openOrder = (channel) => {
    setPricing(null);
    setF({ channel, organization_id: "", po_number: "", contact: "", shipping_address: "", payment_terms: "", currency: "EUR", exchange_rate: 1, notes: "", lines: [{ _k: crypto.randomUUID(), kind: "variant", ref: "", quantity: 1, unit_price: "" }] });
    setOpen(true);
  };

  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [imp, setImp] = useState(null);
  const [priceFilter, setPriceFilter] = useState(true);
  const [impNoStock, setImpNoStock] = useState(false);
  const [creating, setCreating] = useState(false);
  const importShopify = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/orders/import/shopify-pdf/preview", fd);
      const lines = (r.data.lines || []).map((l) => ({ ...l,
        _kind: l.suggested_kind === "bundle" ? "bundle" : "variant",
        _ref: l.suggested_kind === "bundle" ? (l.suggested_bundle_id || "") : (l.suggested_variant_id || "") }));
      setImp({ ...r.data, lines });
    } catch (e) { toast.error(apiError(e)); }
    finally { setImporting(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const impGross = (l) => Number(l.unit_price_gross) || 0;
  const vGross = (v) => Math.round((v.price_net || 0) * (1 + (v.tax_rate ?? 19) / 100) * 100) / 100;
  const vSecondGross = (v) => Math.round((v.price_net_second || 0) * (1 + (v.tax_rate ?? 19) / 100) * 100) / 100;
  const bGross = (b) => b.price_gross != null ? b.price_gross : Math.round((b.price_net || 0) * (1 + (b.tax_rate ?? 19) / 100) * 100) / 100;
  const optsForImp = (l) => {
    const g = impGross(l);
    if (l._kind === "bundle") {
      let arr = bundles; if (priceFilter) arr = arr.filter((b) => Math.abs(bGross(b) - g) <= 0.02);
      return arr.map((b) => ({ value: b.id, label: `${b.name} · ${b.sku} · ${eur(bGross(b))}` }));
    }
    if (l._kind === "second") {
      let arr = variants.filter((v) => Number(v.price_net_second) > 0);
      if (priceFilter) arr = arr.filter((v) => Math.abs(vSecondGross(v) - g) <= 0.02);
      return arr.map((v) => ({ value: v.id, label: `${v.product_name} · ${v.sku} · ${eur(vSecondGross(v))} (${t("ord.secondShort")})` }));
    }
    let arr = variants; if (priceFilter) arr = arr.filter((v) => Math.abs(vGross(v) - g) <= 0.02);
    return arr.map((v) => ({ value: v.id, label: `${v.product_name} · ${v.sku} · ${eur(vGross(v))}` }));
  };
  const updImp = (i, patch) => setImp((p) => ({ ...p, lines: p.lines.map((l, j) => j === i ? { ...l, ...patch } : l) }));
  const createFromImport = async () => {
    setCreating(true);
    try {
      const lines = imp.lines.map((l) => ({ name: l.name, quantity: Number(l.quantity) || 1,
        unit_price_gross: Number(l.unit_price_gross) || 0, tax_rate: Number(l.tax_rate) || 19,
        quality: l._kind === "second" ? "second_choice" : "good", ean: l.ean || "",
        variant_id: l._kind === "bundle" ? null : (l._ref || null),
        bundle_id: l._kind === "bundle" ? (l._ref || null) : null }));
      const r = await api.post("/orders/import/shopify-pdf/confirm", { contact: imp.contact, email: imp.email,
        order_number: imp.order_number, shipping_address: imp.shipping_address, currency: imp.currency, no_stock: impNoStock, lines });
      toast.success(t("ord.importedOk")); setImp(null); nav(`/auftraege/${r.data.id}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setCreating(false); }
  };

  const variantMap = useMemo(() => { const m = {}; variants.forEach((v) => { m[v.id] = v; }); return m; }, [variants]);
  const matchesProduct = useCallback((o, q) => {
    const term = (q || "").trim().toLowerCase();
    if (!term) return true;
    return (o.lines || []).some((l) => {
      const v = variantMap[l.variant_id] || {};
      return `${l.name || ""} ${v.sku || ""} ${v.product_name || ""}`.toLowerCase().includes(term);
    });
  }, [variantMap]);
  const exportProduct = async () => {
    if (!productQ.trim()) return;
    setExporting(true);
    try {
      const params = new URLSearchParams({ product: productQ.trim() });
      if (channelFilter !== "alle") params.set("channel", channelFilter);
      if (showCancelled) params.set("include_cancelled", "true");
      const r = await api.get(`/orders/export/product-search?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `auftraege_${productQ.trim()}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); } finally { setExporting(false); }
  };
  const base = (rows || []).filter((o) => (channelFilter === "alle" || o.channel === channelFilter) && (showCancelled || o.order_status !== "storniert") && matchesProduct(o, productQ));
  const filter = useListFilter(base, { fields: ["order_number", "organization_name", "contact", "channel", "po_number"], dateField: "ordered_at", statusField: "order_status", storageKey: "orders" });
  const filtered = filter.filtered;
  const accessors = useMemo(() => ({
    order_number: (o) => o.order_number,
    channel: (o) => o.channel,
    partner: (o) => o.organization_name || o.contact || "",
    date: (o) => o.ordered_at,
    items: (o) => o.item_count,
    net: (o) => o.net_total,
    gross: (o) => o.gross_total,
    ship_exp: (o) => (o.shipping_expense == null ? -1 : o.shipping_expense),
    order_status: (o) => t(`status.${o.order_status}`, o.order_status),
    payment_status: (o) => t(`status.${o.payment_status}`, o.payment_status),
    fulfillment_status: (o) => t(`status.${o.fulfillment_status}`, o.fulfillment_status),
    no_stock: (o) => (o.no_stock ? 1 : 0),
    revenue_excluded: (o) => (o.revenue_excluded ? 1 : 0),
  }), [t]);
  const { sorted, sortKey, sortDir, toggle } = useSortable(filtered, accessors, "date", "desc");
  const sp = (k) => ({ sortId: k, active: sortKey === k, dir: sortDir, onSort: toggle });
  const cancelledCount = (rows || []).filter((o) => o.order_status === "storniert").length;
  const orderChannels = channels.filter((c) => ["shopify", "b2b"].includes((c.type || "").toLowerCase()));
  const channelNames = ["alle", ...orderChannels.map((c) => c.name)];
  const selType = channelFilter === "alle" ? "alle" : (orderChannels.find((c) => c.name === channelFilter)?.type || channelFilter).toLowerCase();
  const showB2B = channelFilter === "alle" || selType === "b2b";
  const showB2C = channelFilter === "alle" || selType === "shopify";

  return (
    <div>
      <PageHeader title={t("ord.title")} subtitle={t("ord.subtitle")}
        actions={canEdit && (
          <div className="flex flex-wrap gap-2">
            {showB2B && <Button data-testid="new-b2b-order-btn" onClick={() => openOrder("B2B")}><Plus className="h-4 w-4 mr-1.5" /> {t("ord.b2bOrder")}</Button>}
            {showB2C && <Button data-testid="new-b2c-order-btn" variant={showB2B ? "outline" : "default"} onClick={() => openOrder("Shopify")}><Plus className="h-4 w-4 mr-1.5" /> {t("ord.b2cOrder")}</Button>}
            {showB2C && <>
              <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={(e) => importShopify(e.target.files?.[0])} data-testid="import-shopify-input" />
              <Button variant="outline" disabled={importing} onClick={() => fileRef.current?.click()} data-testid="import-shopify-pdf-btn"><Upload className="h-4 w-4 mr-1.5" /> {importing ? t("ord.importingShort") : t("ord.importPdf")}</Button>
            </>}
          </div>
        )} />

      <Tabs value={channelFilter} onValueChange={setChannelFilter} className="mb-4">
        <TabsList className="flex-wrap h-auto">{channelNames.map((c) => <TabsTrigger key={c} value={c} data-testid={`filter-${c}`}>{c === "alle" ? t("common.all") : c}</TabsTrigger>)}</TabsList>
      </Tabs>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button variant={showCancelled ? "default" : "outline"} size="sm" onClick={() => setShowCancelled((v) => !v)} data-testid="toggle-cancelled-orders">
          {showCancelled ? t("ord.hideCancelled") : `${t("ord.showCancelled")}${cancelledCount ? ` (${cancelledCount})` : ""}`}
        </Button>
        <div className="flex items-center gap-2 ml-auto">
          <Input className="h-9 w-56" placeholder="Produkt suchen (z. B. ISOBAG)" value={productQ} onChange={(e) => setProductQ(e.target.value)} data-testid="order-product-search" />
          {productQ.trim() && <span className="text-sm text-muted-foreground whitespace-nowrap" data-testid="order-product-count">{filtered.length} Treffer</span>}
          <Button variant="outline" size="sm" disabled={!productQ.trim() || exporting} onClick={exportProduct} data-testid="order-product-export">
            <Download className="h-4 w-4 mr-1.5" />{exporting ? "Export…" : "Excel-Export"}
          </Button>
        </div>
      </div>

      <ListFilter filter={filter} testId="orders" withDates withStatus placeholder={t("filter.searchOrders")}
        exportName="auftraege" exportColumns={[
          { label: t("ord.colOrder"), value: (o) => o.order_number },
          { label: t("ord.colChannel"), value: (o) => o.channel },
          { label: t("ord.colPartner"), value: (o) => o.organization_name || o.contact || "" },
          { label: t("common.date"), value: (o) => dateFmt(o.ordered_at) },
          { label: t("ord.colItems"), value: (o) => o.item_count },
          { label: t("common.net"), value: (o) => csvMoney(o.net_total) },
          { label: t("common.gross"), value: (o) => csvMoney(o.gross_total) },
          { label: t("ord.colShipExpense"), value: (o) => (o.shipping_expense == null ? "" : csvMoney(o.shipping_expense)) },
          { label: t("ord.colStatus"), value: (o) => t(`status.${o.order_status}`, o.order_status) },
          { label: t("ord.colPayment"), value: (o) => t(`status.${o.payment_status}`, o.payment_status) },
          { label: t("ord.colFulfillment"), value: (o) => t(`status.${o.fulfillment_status}`, o.fulfillment_status) },
        ]} />

      {rows === null ? <div className="text-muted-foreground">{t("common.loading")}</div> :
        filtered.length === 0 ? <EmptyState testId="orders-empty" icon={ShoppingCart} title={t("ord.emptyTitle")} hint={t("ord.emptyHint")} /> : (
          <div className="overflow-x-auto rounded-md border border-border bg-card shadow-sm">
            <table className="w-full">
              <thead className="bg-muted/50"><tr>
                <SortTh {...sp("order_number")}>{t("ord.colOrder")}</SortTh>
                <SortTh {...sp("channel")}>{t("ord.colChannel")}</SortTh>
                <SortTh {...sp("partner")}>{t("ord.colPartner")}</SortTh>
                <SortTh {...sp("date")}>{t("common.date")}</SortTh>
                <SortTh right {...sp("items")}>{t("ord.colItems")}</SortTh>
                <SortTh right {...sp("net")}>{t("common.net")}</SortTh>
                <SortTh right {...sp("gross")}>{t("common.gross")}</SortTh>
                <SortTh right {...sp("ship_exp")}>{t("ord.colShipExpense")}</SortTh>
                <SortTh {...sp("order_status")}>{t("ord.colStatus")}</SortTh>
                <SortTh {...sp("payment_status")}>{t("ord.colPayment")}</SortTh>
                <SortTh {...sp("fulfillment_status")}>{t("ord.colFulfillment")}</SortTh>
                <SortTh {...sp("no_stock")}>{t("od.noStockBadge")}</SortTh>
                <SortTh {...sp("revenue_excluded")}>{t("od.noRevenueBadge")}</SortTh>
              </tr></thead>
              <tbody data-testid="orders-table">
                {sorted.map((o) => (
                  <tr key={o.id} className="hover:bg-muted/40 cursor-pointer transition-colors" onClick={() => nav(`/auftraege/${o.id}`)} data-testid={`order-row-${o.order_number}`}>
                    <Td mono>{o.order_number}{o.messe && <span className="inline-flex items-center align-middle ml-1.5 text-primary" title={t("ord.messeIconTitle")} data-testid={`order-messe-${o.order_number}`}><Store className="h-3.5 w-3.5" /></span>}{o.duplicate_badge && <span onClick={(e) => { e.stopPropagation(); nav(`/auftraege/${o.duplicate_badge.order_id}`); }} className={`inline-flex items-center align-middle ml-1.5 ${o.duplicate_badge.level === "id" ? "text-destructive" : "text-amber-600"}`} title={`${t(o.duplicate_badge.level === "id" ? "ord.dupBadgeConfirmed" : "ord.dupBadgePossible")} · ${o.duplicate_badge.matched_on} → ${o.duplicate_badge.order_number}`} data-testid={`order-duplicate-${o.order_number}`}><Copy className="h-3.5 w-3.5" /></span>}</Td><Td>{o.channel}</Td><Td>{o.organization_name || (o.channel === "Shopify" ? "Shopify" : "—")}</Td><Td>{dateFmt(o.ordered_at)}</Td>
                    <Td right mono>{o.item_count}</Td><Td right mono>{eur(o.net_total)}</Td><Td right mono>{eur(o.gross_total)}</Td>
                    <Td right mono>{o.shipping_expense == null ? "—" : eur(o.shipping_expense)}</Td>
                    <Td><StatusBadge status={o.order_status} /></Td><Td><StatusBadge status={o.payment_status} /></Td><Td><StatusBadge status={o.fulfillment_status} /></Td>
                    <Td>{o.no_stock ? <span className="inline-flex text-chart-3" title={t(o.messe ? "ord.messeNoStockTitle" : "od.noStockBadge")} data-testid={`order-nostock-${o.order_number}`}><PackageX className="h-3.5 w-3.5" /></span> : <span className="text-muted-foreground">—</span>}</Td>
                    <Td>{o.revenue_excluded ? <span className="inline-flex text-chart-3" title={t("ord.messeNoRevenueTitle")} data-testid={`order-norevenue-${o.order_number}`}><ReceiptEuro className="h-3.5 w-3.5" /></span> : <span className="text-muted-foreground">—</span>}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto">
          <DialogHeader><DialogTitle>{f.channel === "Shopify" ? t("ord.newB2C") : t("ord.newB2B")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
            {f.channel === "B2B" ? (<>
            <div><Label>{t("ord.organization")}</Label>
              <Select value={f.organization_id} onValueChange={async (v) => { const o = orgs.find((x) => x.id === v); setF((prev) => ({ ...prev, organization_id: v, payment_terms: o?.payment_terms || "", contact: o?.shipping_contact || o?.contacts?.[0]?.name || "", shipping_address: o?.shipping_address || o?.address || "" })); try { const r = await api.get(`/organizations/${v}/pricing`); setPricing(r.data); } catch { setPricing(null); } }}>
                <SelectTrigger className="mt-1.5" data-testid="order-org"><SelectValue placeholder={t("ord.select")} /></SelectTrigger>
                <SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>{t("ord.poNumber")}</Label><Input data-testid="order-po" value={f.po_number} onChange={(e) => setF({ ...f, po_number: e.target.value })} className="mt-1.5" /></div>
            <div><Label>{t("ord.contactPerson")}</Label>{(() => { const so = orgs.find((x) => x.id === f.organization_id); const cs = so?.contacts || []; return cs.length ? (
              <Select value={f.contact} onValueChange={(v) => setF({ ...f, contact: v })}><SelectTrigger className="mt-1.5" data-testid="order-contact"><SelectValue placeholder={t("ord.chooseContact")} /></SelectTrigger><SelectContent>{so?.shipping_contact && <SelectItem value={so.shipping_contact}>{so.shipping_contact} · {t("ord.shippingAddress")}</SelectItem>}{cs.map((c, i) => <SelectItem key={c.name || i} value={c.name}>{c.name}{c.role ? ` · ${c.role}` : ""}</SelectItem>)}</SelectContent></Select>
            ) : (<Input value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} className="mt-1.5" data-testid="order-contact" />); })()}</div>
            <div><Label>{t("ord.paymentTerms")}</Label><Input value={f.payment_terms} onChange={(e) => setF({ ...f, payment_terms: e.target.value })} className="mt-1.5" /></div>
            </>) : (<>
            <div className="col-span-2"><Label>{t("ord.customerB2C")}</Label><Input data-testid="order-customer" value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} className="mt-1.5"  /></div>
            <div><Label>{t("ord.orderRef")}</Label><Input data-testid="order-po" value={f.po_number} onChange={(e) => setF({ ...f, po_number: e.target.value })} className="mt-1.5"  /></div>
            </>)}
            <div><Label>{t("common.currency")}</Label>
              <Select value={f.currency} onValueChange={(v) => setF({ ...f, currency: v, exchange_rate: ({ EUR: 1, USD: 0.92, CAD: 0.68 })[v] ?? 1 })}>
                <SelectTrigger className="mt-1.5" data-testid="order-currency"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="EUR">EUR (€)</SelectItem><SelectItem value="USD">USD ($)</SelectItem><SelectItem value="CAD">CAD (C$)</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>{t("ord.exchangeRate")}</Label><NumberInput value={f.exchange_rate} onChange={(val) => setF({ ...f, exchange_rate: val })} className="mt-1.5" testId="order-rate" disabled={f.currency === "EUR"} /></div>
            {f.channel === "B2B" && pricing?.price_list_name && <div className="col-span-2 text-xs text-primary">{t("ord.priceListActive", { name: pricing.price_list_name })}</div>}
            {f.channel === "B2B" && !pricing?.price_list_name && pricing?.discount_percent > 0 && <div className="col-span-2 text-xs text-primary">{t("ord.discountApplied", { pct: pricing.discount_percent })}</div>}
            <div className="col-span-2"><Label>{t("ord.shippingAddress")}</Label>{(() => { const so = orgs.find((x) => x.id === f.organization_id); return f.channel === "B2B" && so?.shipping_address ? <span className="ml-2 text-xs text-primary">{t("ord.shippingTaken")}</span> : null; })()}<Textarea value={f.shipping_address} onChange={(e) => setF({ ...f, shipping_address: e.target.value })} className="mt-1.5" data-testid="order-shipping" /></div>
            <TaxCaseCard organizationId={f.organization_id} channel={f.channel} value={f}
              onChange={(patch) => setF((prev) => ({ ...prev, ...patch }))} />
          </div>
          <div className="flex items-center justify-between mt-2"><Label>{t("ord.colItems")}</Label><Button size="sm" variant="outline" onClick={addLine} data-testid="add-order-line"><Plus className="h-3.5 w-3.5 mr-1" /> {t("ord.colItems")}</Button></div>
          <div className="space-y-2">
            {f.lines.map((l, i) => (
              <div key={l._k || i} className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <Select value={l.kind} onValueChange={(v) => upd(i, "kind", v)}><SelectTrigger className="w-28 shrink-0"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="variant">{t("bundle.chooseProduct")}</SelectItem><SelectItem value="bundle">{t("bundle.title")}</SelectItem></SelectContent></Select>
                {l.kind === "variant" && (
                  <Select value={l.quality || "good"} onValueChange={(v) => upd(i, "quality", v)}>
                    <SelectTrigger className="w-28 shrink-0" data-testid={`order-line-quality-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="good">{t("quality.good")}</SelectItem><SelectItem value="second_choice">{t("quality.second_choice")}</SelectItem></SelectContent>
                  </Select>
                )}
                <div className="flex-1 min-w-[180px]">
                <SearchSelect
                  testId={`order-line-ref-${i}`}
                  value={l.ref}
                  onChange={(v) => upd(i, "ref", v)}
                  placeholder={t("ord.select")}
                  options={lineOptions(l)}
                />
                </div>
                <NumberInput className="w-16 shrink-0" placeholder={t("common.quantity")} value={l.quantity} onChange={(val) => upd(i, "quantity", val)} testId={`order-line-qty-${i}`} />
                <NumberInput className="w-24 shrink-0" decimals={2} placeholder={t("op.unitPrice")} value={l.unit_price} onChange={(val) => upd(i, "unit_price", val)} testId={`order-line-price-${i}`} />
                <Button size="icon" variant="ghost" className="shrink-0" onClick={() => rm(i)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
              {l._prov?.pricing_source === "promotion" && !l._manual && (
                <div className="ml-1 flex flex-wrap items-center gap-x-2 rounded-md border border-primary/40 bg-primary/5 px-2 py-1.5 text-xs" data-testid={`order-line-promo-${i}`}>
                  <span className="font-medium text-primary">{t("pv.src.promotion")}: {l._prov.promotion_name_snapshot}</span>
                  <span className="text-muted-foreground">{t("sp.regularPrice")} {eur(l._prov.promotion_base_price)} → </span>
                  <span className="font-mono text-foreground">{eur(l._prov.unit_price)}</span>
                  {l._prov.underlying_price_source === "price_list" && <span className="text-muted-foreground">({t("pv.src.price_list")})</span>}
                </div>
              )}
              {l._manual && (
                <div className="ml-1 flex items-center gap-2 rounded-md border border-amber-300/60 bg-amber-50 px-2 py-1.5" data-testid={`order-line-override-${i}`}>
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-600" />
                  <span className="shrink-0 text-xs text-amber-800">
                    {t("ord.priceOverrideHint")}{priceFor(l.kind, l.ref, l.quality) !== "" ? ` ${Number(priceFor(l.kind, l.ref, l.quality)).toFixed(2).replace(".", ",")} €` : ""}
                  </span>
                  <Input className="h-7 flex-1 text-xs" placeholder={t("ord.priceOverrideReason")} value={l.override_reason || ""} onChange={(e) => upd(i, "override_reason", e.target.value)} data-testid={`order-line-override-reason-${i}`} />
                </div>
              )}
            </div>
            ))}
          </div>
          <DialogFooter><Button data-testid="save-order-btn" onClick={save} disabled={!f.lines.length || f.lines.some((l) => !l.ref)}>{t("ord.createOrder")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!imp} onOpenChange={(o) => { if (!o) setImp(null); }}>
        <DialogContent className="max-w-3xl max-h-[90dvh] overflow-y-auto">
          <DialogHeader><DialogTitle>{t("ord.importTitle")}</DialogTitle></DialogHeader>
          {imp && (
            <div className="space-y-4">
              <div className="rounded-md bg-muted/40 p-3 text-sm space-y-0.5" data-testid="import-header">
                <div><span className="text-muted-foreground">{t("ord.customer")}:</span> {imp.contact}{imp.email ? ` · ${imp.email}` : ""}</div>
                {imp.order_number && <div><span className="text-muted-foreground">{t("ord.orderNo")}:</span> {imp.order_number}</div>}
                {imp.shipping_address && <div className="whitespace-pre-line"><span className="text-muted-foreground">{t("ord.shippingAddress")}:</span> {imp.shipping_address}</div>}
              </div>
              {imp.duplicate && imp.duplicate.level === "id" && (
                <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive space-y-1" data-testid="import-dup-warn">
                  <div><strong>{t("ord.dupImported")}</strong> ({imp.duplicate.matched_on}, {imp.duplicate.order_number})</div>
                  <button type="button" className="underline font-medium" onClick={() => { const id = imp.duplicate.order_id; setImp(null); nav(`/auftraege/${id}`); }} data-testid="import-dup-open">{t("ord.openExisting")}</button>
                </div>
              )}
              {imp.duplicate && imp.duplicate.level === "name" && (
                <div className="rounded-md border border-chart-4/40 bg-chart-4/5 p-3 text-sm" data-testid="import-dup-info">
                  {t("ord.dupHint")} ({imp.duplicate.matched_on}, {imp.duplicate.order_number})
                </div>
              )}
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={priceFilter} onChange={(e) => setPriceFilter(e.target.checked)} data-testid="import-price-filter" />
                {t("ord.priceFilter")}
              </label>
              <label className="flex items-center gap-2 text-sm rounded-md bg-chart-3/5 border border-chart-3/30 p-2" data-testid="import-nostock-label">
                <input type="checkbox" checked={impNoStock} onChange={(e) => setImpNoStock(e.target.checked)} data-testid="import-no-stock" />
                <span><strong>{t("ord.noStock")}</strong> {t("ord.noStockHint")}</span>
              </label>
              <div className="space-y-3">
                {imp.lines.map((l, i) => (
                  <div key={i} className="rounded-md border border-border p-3 space-y-2" data-testid={`import-line-${i}`}>
                    <div className="flex items-center justify-between gap-2 text-sm">
                      <div className="font-medium">{l.name}</div>
                      <div className="text-muted-foreground shrink-0">{t("common.quantity")} {l.quantity} · {eur(impGross(l))} {t("common.gross")}</div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Select value={l._kind} onValueChange={(v) => updImp(i, { _kind: v, _ref: "" })}>
                        <SelectTrigger className="w-28 shrink-0" data-testid={`import-kind-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="variant">{t("bundle.chooseProduct")}</SelectItem><SelectItem value="bundle">{t("bundle.title")}</SelectItem><SelectItem value="second">{t("ord.secondShort")}</SelectItem></SelectContent>
                      </Select>
                      <div className="flex-1 min-w-[220px]">
                        <SearchSelect testId={`import-ref-${i}`} value={l._ref} onChange={(v) => updImp(i, { _ref: v })} placeholder={t("ord.chooseProductBundle")} options={optsForImp(l)} />
                      </div>
                    </div>
                    {l.suggested_sku
                      ? <div className="text-xs text-chart-4">{t("ord.suggestion")}: {l.suggested_sku}{l.match_reason ? ` (${l.match_reason}${l.match_confidence ? `, ${Math.round(l.match_confidence * 100)}%` : ""})` : ""}</div>
                      : <div className="text-xs text-muted-foreground">{t("ord.noSuggestion")}</div>}
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">{t("ord.assignLater")}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setImp(null)} data-testid="import-cancel-btn">{t("common.cancel")}</Button>
            <Button onClick={createFromImport} disabled={creating || imp?.duplicate?.level === "id"} data-testid="import-create-btn">{creating ? t("ord.creating") : t("ord.createOrder")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
